import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { onSnapshot, orderBy, query } from "firebase/firestore";
import { useAuth } from "../auth";
import { dishTitle, useI18n } from "../lib/i18n";
import type { DishDoc, UserDoc } from "../lib/types";
import { scoreBand } from "../lib/evaluation";
import {
  ICHININMAE_SCORE,
  buildFocus,
  buildKpis,
  buildMatrix,
  buildPeople,
  buildTrend,
  type Dish,
  type EvaluationRow,
  type Person,
  type TrendSeries,
} from "../lib/proficiency";

/**
 * 習熟度ダッシュボード(⑨)。
 *
 * 見せ方は商談デモ(ichininmae-web public/demo)を正とする。
 * 中身は Firestore の evaluations の実測だけで組む。デモに出ている
 * 「作業時間◯倍」「包丁の入れ直し◯回」は算出していないため出さない。
 * 料理長・管理者のみ(新人が他人の点数を見られないのは決定2のとおり)。
 */
export function Proficiency() {
  const { profile, paths } = useAuth();
  const { t, lang } = useI18n();
  const [users, setUsers] = useState<(Person & { role: UserDoc["role"] })[] | null>(null);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [evaluations, setEvaluations] = useState<EvaluationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [trendDishId, setTrendDishId] = useState("");

  const isStaff = profile?.role === "admin" || profile?.role === "chef";

  useEffect(() => {
    if (!isStaff || !paths) return;
    return onSnapshot(
      paths.users(),
      (snap) =>
        setUsers(
          snap.docs
            .map((d) => ({ uid: d.id, ...(d.data() as UserDoc) }))
            .map((u) => ({
              uid: u.uid,
              displayName: u.displayName || u.email || u.uid,
              role: u.role,
            })),
        ),
      (e) => setError(e.message),
    );
  }, [isStaff, paths]);

  useEffect(() => {
    if (!isStaff || !paths) return;
    return onSnapshot(query(paths.dishes(), orderBy("updatedAt", "desc")), (snap) =>
      setDishes(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DishDoc) }))
          .filter((d) => d.currentAnalysisPath && !d.deletedAt) // 削除済みの品目は列に出さない
          .map((d) => ({ id: d.id, name: d.name, nameZh: d.nameZh ?? null })),
      ),
    );
  }, [isStaff, paths]);

  useEffect(() => {
    if (!isStaff || !paths) return;
    return onSnapshot(
      query(paths.evaluations(), orderBy("createdAt", "desc")),
      (snap) =>
        setEvaluations(
          snap.docs
            // 削除した評価(論理削除)は熟達度に数えない
            .filter((d) => !d.get("deletedAt"))
            .map((d) => {
            const v = d.data() as {
              dishId: string;
              dishName: string | null;
              traineeUid: string;
              score: number | null;
              status: EvaluationRow["status"];
              createdAt: { toDate(): Date } | null;
            };
            return {
              id: d.id,
              dishId: v.dishId,
              dishName: v.dishName,
              traineeUid: v.traineeUid,
              score: v.score,
              status: v.status,
              createdAt: v.createdAt ? v.createdAt.toDate() : null,
            };
          }),
        ),
      (e) => setError(e.message),
    );
  }, [isStaff, paths]);

  // 行に出す人の決め方は lib 側(buildPeople)が正。役割では絞らない(#124)
  const people = useMemo<Person[] | null>(
    () => (users === null ? null : buildPeople(users, evaluations)),
    [users, evaluations],
  );

  const matrix = useMemo(
    () => buildMatrix(evaluations, people ?? [], dishes),
    [evaluations, people, dishes],
  );
  const kpis = useMemo(() => buildKpis(matrix, evaluations, new Date()), [matrix, evaluations]);
  const focus = useMemo(() => buildFocus(matrix, dishes), [matrix, dishes]);

  // 既定は採点回数のいちばん多い品目(見るべきものが最初に出る)
  const defaultDishId = useMemo(() => {
    let best = "";
    let max = 0;
    for (const d of dishes) {
      const n = evaluations.filter((e) => e.dishId === d.id && e.status === "succeeded").length;
      if (n > max) {
        max = n;
        best = d.id;
      }
    }
    return best;
  }, [dishes, evaluations]);
  const shownDishId = trendDishId || defaultDishId;
  const trend = useMemo(() => buildTrend(matrix, shownDishId), [matrix, shownDishId]);

  if (!isStaff) {
    return (
      <div className="page">
        <h1>{t("習熟度ダッシュボード")}</h1>
        <p className="muted">
          {t("この画面は料理長・管理者のみが利用できます。ご自身の採点結果は")}
          <Link to="/evaluations">{t("「作業の評価」")}</Link>{t("から確認できます。")}
        </p>
      </div>
    );
  }

  return (
    <div className="page wide">
      <header className="page-head">
        <h1>{t("習熟度ダッシュボード")}</h1>
        <span className="muted small">{t("スタッフ × 品目｜お手本との比較採点")}</span>
      </header>
      {error && <p className="error">{error}</p>}

      <div className="kpis">
        <div className="kpi">
          <div className="kpi-label">{t("平均 習熟度")}</div>
          <div className="kpi-num">
            {kpis.average ?? "—"}
            {kpis.average != null && <small>{t("点")}</small>}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t("一人前 到達")}</div>
          <div className="kpi-num">
            {kpis.ichininmaeCells}
            <small>/ {kpis.scoredCells} {t("件")}</small>
          </div>
          <div className="kpi-foot">{t("スタッフ×品目")}</div>
        </div>
        <div className={`kpi ${kpis.needsFocus > 0 ? "alert" : ""}`}>
          <div className="kpi-label">{t("要重点指導")}</div>
          <div className="kpi-num">
            {kpis.needsFocus}
            <small>{t("件")}</small>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t("今月の採点回数")}</div>
          <div className="kpi-num">
            {kpis.thisMonth}
            <small>{t("回")}</small>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>{t("習熟度マトリクス")}</h2>
        {people === null ? (
          <p className="muted">{t("読み込み中…")}</p>
        ) : people.length === 0 || dishes.length === 0 ? (
          <p className="muted">
            {t("採点対象がまだありません。マニュアルが作成済みの品目と、採点が1件以上必要です。")}
          </p>
        ) : (
          <>
            <div className="matrix-wrap">
              <table className="matrix">
                <thead>
                  <tr>
                    <th className="staff-col">{t("スタッフ")}</th>
                    {dishes.map((d) => (
                      <th key={d.id}>{dishTitle(d, lang)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row) => (
                    <tr key={row.person.uid}>
                      <th className="staff-col">{row.person.displayName}</th>
                      {row.cells.map((c) => (
                        <td key={c.dishId}>
                          {c.latest == null ? (
                            <span className="muted small">—</span>
                          ) : c.ichininmae ? (
                            <Link to={c.latestId ? `/evaluations/${c.latestId}` : "#"} className="hanko-link">
                              <span className="hanko">{t("一人前")}</span>
                            </Link>
                          ) : (
                            <Link to={c.latestId ? `/evaluations/${c.latestId}` : "#"} className="cell-link">
                              <span className={`score band-${scoreBand(c.latest)}`}>{c.latest}</span>
                              <span className="trend">
                                {c.delta == null
                                  ? t("初回採点")
                                  : `${c.delta > 0 ? "▲ +" : c.delta < 0 ? "▼ " : "→ "}${c.delta === 0 ? "" : c.delta}`}
                              </span>
                            </Link>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="legend">
              <span><b className="b-good">{t("80点以上")}</b> {t("良好")}</span>
              <span><b className="b-mid">{t("60–79点")}</b> {t("継続指導")}</span>
              <span><b className="b-low">{t("60点未満")}</b> {t("要重点指導")}</span>
              <span><b className="b-shu">{t("朱印")}</b> {t("＝ 一人前到達（{n}点以上を2回連続）", { n: ICHININMAE_SCORE })}</span>
              <span>{t("点数を押すと、その回の評価結果が開きます")}</span>
            </div>
          </>
        )}
      </div>

      <div className="grid2">
        <div className="card">
          <h2>{t("習熟度の推移")}</h2>
          <label htmlFor="trend-dish">{t("品目")}</label>
          <select
            id="trend-dish"
            value={shownDishId}
            onChange={(e) => setTrendDishId(e.target.value)}
          >
            {dishes.map((d) => (
              <option key={d.id} value={d.id}>
                {dishTitle(d, lang)}
              </option>
            ))}
          </select>
          {trend.length === 0 ? (
            <p className="muted small">{t("この品目の採点はまだありません。")}</p>
          ) : (
            <TrendChart series={trend} t={t} />
          )}
        </div>

        <div className="card">
          <h2>{t("要重点指導")}</h2>
          {focus.length === 0 ? (
            <p className="muted small">
              {t("80点未満の組み合わせはありません。")}
            </p>
          ) : (
            focus.map((f, i) => (
              <div key={i} className="focus-item">
                <span className={`tag ${f.level === "focus" ? "missing" : "differs"}`}>
                  {f.level === "focus" ? t("要指導") : t("経過観察")}
                </span>
                <div className="focus-body">
                  <b>
                    {f.person.displayName} × {dishTitle(f.dish, lang)}
                  </b>
                  <span className="why">
                    {t("最新 {n}点", { n: f.score })}
                    {f.delta != null && t("（前回比 {n}）", { n: `${f.delta > 0 ? "+" : ""}${f.delta}` })}
                    {f.evaluationId && (
                      <>
                        　<Link to={`/evaluations/${f.evaluationId}`}>{t("評価結果を見る")}</Link>
                      </>
                    )}
                  </span>
                </div>
              </div>
            ))
          )}
          <p className="muted small">
            {t("どの工程を直すべきかは、各評価結果の「直したいところ」に出ています。")}
          </p>
        </div>
      </div>
    </div>
  );
}

const LINE_COLORS = ["#1E3A5F", "#B98A2F", "#C73E3A", "#4E7A3A", "#6B6A63"];

/** 週次ではなく採点回の順で引く(採点の間隔は現場ごとに違う) */
function TrendChart({
  series,
  t,
}: {
  series: TrendSeries[];
  t: (ja: string, params?: Record<string, string | number>) => string;
}) {
  const W = 520;
  const H = 240;
  const L = 46;
  const R = 20;
  const T = 20;
  const B = 40;
  const maxLen = Math.max(...series.map((s) => s.points.length));
  const x = (i: number) =>
    maxLen <= 1 ? L + (W - L - R) / 2 : L + ((W - L - R) * i) / (maxLen - 1);
  const y = (score: number) => T + (H - T - B) * (1 - (score - 40) / 60);

  return (
    <>
      <div className="chart-keys">
        {series.map((s, i) => (
          <span key={s.person.uid} className="key">
            <span className="sw" style={{ background: LINE_COLORS[i % LINE_COLORS.length] }} />
            {s.person.displayName}
          </span>
        ))}
        <span className="key">
          <span className="sw dashed" />
          {t("一人前ライン（{n}点）", { n: ICHININMAE_SCORE })}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={t("習熟度の推移")}>
        <line x1={L} y1={T} x2={L} y2={H - B} stroke="#E4E1D8" />
        <line x1={L} y1={H - B} x2={W - R} y2={H - B} stroke="#E4E1D8" />
        <line x1={L} y1={y(ICHININMAE_SCORE)} x2={W - R} y2={y(ICHININMAE_SCORE)} stroke="#8A8880" strokeDasharray="4 4" />
        {[100, 90, 70, 50].map((v) => (
          <text key={v} x={L - 6} y={y(v) + 4} fontSize="10" fill="#8A8880" textAnchor="end">
            {v}
          </text>
        ))}
        {Array.from({ length: maxLen }, (_, i) => (
          <text key={i} x={x(i)} y={H - B + 18} fontSize="10" fill="#8A8880" textAnchor="middle">
            {t("{n}回目", { n: i + 1 })}
          </text>
        ))}
        {series.map((s, si) => {
          const color = LINE_COLORS[si % LINE_COLORS.length];
          const pts = s.points.map((p, i) => `${x(i)},${y(p.score)}`).join(" ");
          return (
            <g key={s.person.uid}>
              {s.points.length > 1 && (
                <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
              )}
              {s.points.map((p, i) => (
                <circle key={i} cx={x(i)} cy={y(p.score)} r="3.5" fill={color} />
              ))}
            </g>
          );
        })}
      </svg>
    </>
  );
}
